import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Trash2, Edit, Settings } from 'lucide-react';
import { toast } from 'sonner';

import { 
  FIELD_TYPES, 
  CONDITION_OPERATORS, 
  PREDEFINED_LISTS, 
  REPEATABLE_GROUP_TEMPLATES 
} from './constants';

const FieldEditorModal = ({ open, onClose, onSave, initialField, formConfig }) => {
  const [fieldForm, setFieldForm] = useState({
    id: '',
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    help_text: '',
    options: [],
    predefined_list: '',
    accept_file_types: '',
    group_template: 'custom',
    sub_fields: [],
    min_value: null,
    max_value: null,
    min_chars: null,
    max_chars: null,
    conditional: null
  });

  const [newOption, setNewOption] = useState('');
  const [showConditionalConfig, setShowConditionalConfig] = useState(false);
  const [newSubField, setNewSubField] = useState({ label: '', type: 'text', options: [] });
  const [newSubFieldOption, setNewSubFieldOption] = useState('');

  useEffect(() => {
    if (open) {
      if (initialField) {
        setFieldForm({ ...initialField });
        setShowConditionalConfig(!!initialField.conditional);
      } else {
        setFieldForm({
          id: `field_${Date.now()}`,
          label: '',
          type: 'text',
          required: false,
          placeholder: '',
          help_text: '',
          options: [],
          predefined_list: '',
          accept_file_types: '',
          group_template: 'custom',
          sub_fields: [],
          min_value: null,
          max_value: null,
          min_chars: null,
          max_chars: null,
          conditional: null
        });
        setShowConditionalConfig(false);
      }
      setNewOption('');
    }
  }, [open, initialField]);

  const saveField = () => {
    if (!fieldForm.label) {
      toast.error("Field label is required");
      return;
    }
    
    // Clean up conditional configuration if disabled
    const fieldData = { ...fieldForm };
    if (!showConditionalConfig || !fieldData.conditional?.field_id) {
      fieldData.conditional = null;
    }

    onSave(fieldData);
  };

  const addOption = () => {
    if (newOption.trim()) {
      setFieldForm({
        ...fieldForm,
        options: [...fieldForm.options, newOption.trim()]
      });
      setNewOption('');
    }
  };

  const removeOption = (index) => {
    const newOptions = [...fieldForm.options];
    newOptions.splice(index, 1);
    setFieldForm({ ...fieldForm, options: newOptions });
  };

  const addCustomSubField = () => {
    if (!newSubField.label) {
      toast.error('Sub-field label is required');
      return;
    }
    const subField = {
      id: newSubField.label.toLowerCase().replace(/[^a-z0-9]/g, '_'),
      label: newSubField.label,
      type: newSubField.type,
      required: false,
      options: newSubField.options || []
    };
    
    setFieldForm({
      ...fieldForm,
      sub_fields: [...(fieldForm.sub_fields || []), subField]
    });
    setNewSubField({ label: '', type: 'text', options: [] });
  };

  const removeCustomSubField = (index) => {
    const updated = [...fieldForm.sub_fields];
    updated.splice(index, 1);
    setFieldForm({ ...fieldForm, sub_fields: updated });
  };

  const addSubFieldOption = () => {
    if (newSubFieldOption.trim()) {
      setNewSubField({
        ...newSubField,
        options: [...(newSubField.options || []), newSubFieldOption.trim()]
      });
      setNewSubFieldOption('');
    }
  };

  const removeSubFieldOption = (index) => {
    const updated = [...(newSubField.options || [])];
    updated.splice(index, 1);
    setNewSubField({ ...newSubField, options: updated });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {initialField ? 'Edit Field' : 'Add Field'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Field Label *</Label>
            <Input
              value={fieldForm.label}
              onChange={(e) => {
                const label = e.target.value;
                const id = initialField ? fieldForm.id : label.toLowerCase().replace(/[^a-z0-9]/g, '_');
                setFieldForm({ ...fieldForm, label, id });
              }}
              placeholder="e.g., First Name"
              className="rounded-sm"
            />
          </div>

          <div className="space-y-2">
            <Label>Field ID (System Name)</Label>
            <Input
              value={fieldForm.id}
              onChange={(e) => setFieldForm({ ...fieldForm, id: e.target.value })}
              className="font-mono text-sm rounded-sm"
              disabled={!!initialField}
            />
          </div>

          <div className="space-y-2">
            <Label>Field Type</Label>
            <Select
              value={fieldForm.type}
              onValueChange={(value) => setFieldForm({ ...fieldForm, type: value })}
            >
              <SelectTrigger className="rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Conditional Logic Configuration */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold text-slate-700 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Conditional Logic
              </Label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showConditionalConfig}
                  onChange={(e) => {
                    setShowConditionalConfig(e.target.checked);
                    if (e.target.checked && !fieldForm.conditional) {
                      setFieldForm({
                        ...fieldForm,
                        conditional: { field_id: '', operator: 'equals', value: '' }
                      });
                    }
                  }}
                  className="rounded border-slate-300 text-greek-blue focus:ring-greek-blue"
                />
                Enable Conditional Display
              </label>
            </div>
            
            {showConditionalConfig && (
              <div className="space-y-3 mt-3 pt-3 border-t border-slate-200">
                <p className="text-xs text-amber-700">This field will only be visible when the condition is met</p>
                
                {(() => {
                  const allFields = [];
                  formConfig.forEach(section => {
                    section.fields?.forEach(f => {
                      if (f.id !== fieldForm.id) {
                        allFields.push({ ...f, sectionTitle: section.title });
                      }
                    });
                  });

                  return (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Depends on Field</Label>
                        <Select
                          value={fieldForm.conditional?.field_id || ''}
                          onValueChange={(value) => setFieldForm({
                            ...fieldForm,
                            conditional: { ...fieldForm.conditional, field_id: value, operator: 'equals', value: '' }
                          })}
                        >
                          <SelectTrigger className="w-full text-xs">
                            <SelectValue placeholder="Select a field" />
                          </SelectTrigger>
                          <SelectContent>
                            {formConfig.map(section => (
                              <optgroup key={section.id} label={section.title}>
                                {section.fields?.filter(f => f.id !== fieldForm.id).map(f => (
                                  <SelectItem key={f.id} value={f.id}>{f.label} ({f.type})</SelectItem>
                                ))}
                              </optgroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {fieldForm.conditional?.field_id && (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Condition</Label>
                            <Select
                              value={fieldForm.conditional?.operator || 'equals'}
                              onValueChange={(value) => setFieldForm({
                                ...fieldForm,
                                conditional: { ...fieldForm.conditional, operator: value }
                              })}
                            >
                              <SelectTrigger className="w-full text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CONDITION_OPERATORS.map(op => (
                                  <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {['equals', 'not_equals', 'contains'].includes(fieldForm.conditional?.operator) && (
                            <div className="space-y-1">
                              <Label className="text-xs">Value</Label>
                              {(() => {
                                const dependentField = allFields.find(f => f.id === fieldForm.conditional?.field_id);
                                
                                if (dependentField && ['select', 'radio', 'dropdown_list'].includes(dependentField.type)) {
                                  const options = dependentField.type === 'dropdown_list' 
                                    ? (PREDEFINED_LISTS[dependentField.predefined_list]?.options || [])
                                    : (dependentField.options || []);
                                    
                                  return (
                                    <Select
                                      value={fieldForm.conditional?.value || ''}
                                      onValueChange={(value) => setFieldForm({
                                        ...fieldForm,
                                        conditional: { ...fieldForm.conditional, value }
                                      })}
                                    >
                                      <SelectTrigger className="w-full text-xs">
                                        <SelectValue placeholder="Select value" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {options.map((opt, idx) => (
                                          <SelectItem key={idx} value={opt}>{opt}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  );
                                } else if (dependentField && dependentField.type === 'checkbox') {
                                  return (
                                    <Select
                                      value={fieldForm.conditional?.value || ''}
                                      onValueChange={(value) => setFieldForm({
                                        ...fieldForm,
                                        conditional: { ...fieldForm.conditional, value }
                                      })}
                                    >
                                      <SelectTrigger className="w-full text-xs">
                                        <SelectValue placeholder="Select value" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="true">Checked (Yes)</SelectItem>
                                        <SelectItem value="false">Unchecked (No)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  );
                                } else {
                                  return (
                                    <Input
                                      value={fieldForm.conditional?.value || ''}
                                      onChange={(e) => setFieldForm({
                                        ...fieldForm,
                                        conditional: { ...fieldForm.conditional, value: e.target.value }
                                      })}
                                      className="h-8 text-xs"
                                      placeholder="Match value..."
                                    />
                                  );
                                }
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="required"
              checked={fieldForm.required}
              onCheckedChange={(checked) => setFieldForm({ ...fieldForm, required: checked })}
            />
            <Label htmlFor="required">Required Field</Label>
          </div>

          {(fieldForm.type === 'text' || fieldForm.type === 'textarea') && (
            <div className="space-y-2">
              <Label>Placeholder</Label>
              <Input
                value={fieldForm.placeholder}
                onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })}
                className="rounded-sm"
              />
            </div>
          )}
          
          {(fieldForm.type === 'text' || fieldForm.type === 'textarea' || fieldForm.type === 'richtext') && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Characters</Label>
                <Input
                  type="number"
                  value={fieldForm.min_chars || ''}
                  onChange={(e) => setFieldForm({ ...fieldForm, min_chars: e.target.value ? parseInt(e.target.value) : null })}
                  className="rounded-sm"
                  placeholder="e.g. 50"
                  min="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Characters</Label>
                <Input
                  type="number"
                  value={fieldForm.max_chars || ''}
                  onChange={(e) => setFieldForm({ ...fieldForm, max_chars: e.target.value ? parseInt(e.target.value) : null })}
                  className="rounded-sm"
                  placeholder="e.g. 500"
                  min="0"
                />
              </div>
            </div>
          )}
          
          {fieldForm.type === 'number' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum Value</Label>
                <Input
                  type="number"
                  value={fieldForm.min_value || ''}
                  onChange={(e) => setFieldForm({ ...fieldForm, min_value: e.target.value ? parseInt(e.target.value) : null })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Maximum Value</Label>
                <Input
                  type="number"
                  value={fieldForm.max_value || ''}
                  onChange={(e) => setFieldForm({ ...fieldForm, max_value: e.target.value ? parseInt(e.target.value) : null })}
                  className="rounded-sm"
                />
              </div>
            </div>
          )}
          
          {fieldForm.type === 'dropdown_list' && (
            <div className="space-y-2">
              <Label>Predefined List</Label>
              <Select
                value={fieldForm.predefined_list || ''}
                onValueChange={(value) => setFieldForm({ ...fieldForm, predefined_list: value })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select a list" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PREDEFINED_LISTS).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {fieldForm.type === 'file' && (
            <div className="space-y-2">
              <Label>Accepted File Types</Label>
              <Input
                value={fieldForm.accept_file_types || ''}
                onChange={(e) => setFieldForm({ ...fieldForm, accept_file_types: e.target.value })}
                placeholder="e.g., .pdf,.doc,.docx"
                className="rounded-sm"
              />
              <p className="text-xs text-slate-500">Leave blank to accept all common document types. Comma separated (e.g., .pdf,.jpg,.png)</p>
            </div>
          )}

          {fieldForm.type === 'repeatable_group' && (
            <div className="space-y-4 border p-4 rounded-md bg-slate-50">
              <div className="space-y-2">
                <Label>Group Template</Label>
                <Select
                  value={fieldForm.group_template || 'custom'}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setFieldForm({ ...fieldForm, group_template: value, sub_fields: [] });
                    } else {
                      const template = REPEATABLE_GROUP_TEMPLATES[value];
                      setFieldForm({ 
                        ...fieldForm, 
                        group_template: value,
                        sub_fields: template.sub_fields
                      });
                    }
                  }}
                >
                  <SelectTrigger className="rounded-sm bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(REPEATABLE_GROUP_TEMPLATES).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldForm.group_template !== 'custom' && (
                  <p className="text-xs text-slate-500 mt-1">
                    {REPEATABLE_GROUP_TEMPLATES[fieldForm.group_template]?.description}
                  </p>
                )}
              </div>

              {/* Sub-fields editor (visible only for custom template) */}
              {fieldForm.group_template === 'custom' && (
                <div className="space-y-4 pt-2 border-t border-slate-200">
                  <Label>Define Custom Sub-fields</Label>
                  
                  {fieldForm.sub_fields && fieldForm.sub_fields.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {fieldForm.sub_fields.map((sub, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border text-sm">
                          <div>
                            <span className="font-medium">{sub.label}</span>
                            <span className="text-slate-500 ml-2">({sub.type})</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeCustomSubField(idx)}
                            className="text-red-500 h-6 px-2"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="bg-white p-3 rounded border space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Sub-field Label</Label>
                        <Input 
                          size="sm" 
                          value={newSubField.label}
                          onChange={(e) => setNewSubField({...newSubField, label: e.target.value})}
                          placeholder="e.g. End Year" 
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={newSubField.type}
                          onValueChange={(value) => setNewSubField({...newSubField, type: value})}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">Text Input</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                            <SelectItem value="select">Dropdown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {newSubField.type === 'select' && (
                      <div className="space-y-2 pt-2 border-t">
                        <Label className="text-xs">Dropdown Options</Label>
                        <div className="flex gap-2">
                          <Input
                            value={newSubFieldOption}
                            onChange={(e) => setNewSubFieldOption(e.target.value)}
                            placeholder="Add option..."
                            className="h-8 text-xs"
                            onKeyPress={(e) => e.key === 'Enter' && addSubFieldOption()}
                          />
                          <Button type="button" onClick={addSubFieldOption} size="sm" className="h-8">Add</Button>
                        </div>
                        {newSubField.options?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {newSubField.options.map((opt, i) => (
                              <span key={i} className="bg-slate-100 text-xs px-2 py-1 rounded flex items-center gap-1">
                                {opt}
                                <button type="button" onClick={() => removeSubFieldOption(i)} className="text-red-500 hover:text-red-700">&times;</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <Button 
                      type="button" 
                      onClick={addCustomSubField}
                      variant="outline"
                      size="sm"
                      className="w-full text-xs h-8"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Sub-field
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {(fieldForm.type === 'select' || fieldForm.type === 'radio' || fieldForm.type === 'checkbox_group') && (
            <div className="space-y-2">
              <Label>Options</Label>
              <div className="flex gap-2">
                <Input
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder="Add an option"
                  onKeyPress={(e) => e.key === 'Enter' && addOption()}
                  className="rounded-sm"
                />
                <Button onClick={addOption} type="button" className="rounded-sm">Add</Button>
              </div>
              <div className="space-y-2 mt-2">
                {fieldForm.options.map((option, index) => (
                  <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded-sm">
                    <span className="text-sm">{option}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOption(index)}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Help Text</Label>
            <Textarea
              value={fieldForm.help_text}
              onChange={(e) => setFieldForm({ ...fieldForm, help_text: e.target.value })}
              placeholder="Instructions or help text for applicants"
              className="rounded-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={saveField} className="bg-greek-blue hover:bg-greek-blue-dark rounded-sm">Save Field</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FieldEditorModal;